import styled, { css } from "styled-components";

const font = css`
  font-family: 'Aileron', 'Poppins', sans-serif;
`;

export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

// On narrow screens each row becomes a stacked card. Cells show their column
// name from data-label; the first, unlabelled cell is the row's heading.
export const Table = styled.table`
  ${font}
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  color: #2a241f;

  @media (max-width: 700px) {
    thead {
      display: none;
    }

    tr {
      display: block;
      padding: 12px 0;
      border-bottom: 1px solid #f3eee7;
    }

    td {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 5px 0;
      border-bottom: none;
      white-space: normal;
      text-align: right;
      min-width: 0;
      overflow-wrap: anywhere;
    }

    td[data-label]::before {
      content: attr(data-label);
      flex-shrink: 0;
      font-size: 12px;
      font-weight: 600;
      color: #9a8e84;
      text-align: left;
    }

    td:not([data-label]) {
      display: block;
      text-align: left;
      padding-bottom: 6px;
    }

    td:not([data-label]):empty {
      display: none;
    }
  }
`;

export const Th = styled.th<{ $align?: "left" | "right" | "center" }>`
  ${font}
  text-align: ${({ $align }) => $align || "left"};
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9a8e84;
  padding: 0 12px 10px;
  white-space: nowrap;
  border-bottom: 1px solid #ece4da;

  &:first-child {
    padding-left: 0;
  }
`;

export const Td = styled.td<{ $align?: "left" | "right" | "center"; $muted?: boolean }>`
  text-align: ${({ $align }) => $align || "left"};
  padding: 12px;
  border-bottom: 1px solid #f3eee7;
  vertical-align: middle;
  color: ${({ $muted }) => ($muted ? "#8a7f74" : "#2a241f")};
  white-space: nowrap;

  &:first-child {
    padding-left: 0;
  }
`;

export const Tr = styled.tr<{ $clickable?: boolean }>`
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover td {
        background: #faf8f5;
      }
    `}
`;

export const PrimaryText = styled.div`
  font-weight: 600;
  color: #111111;
`;

export const SecondaryText = styled.div`
  font-size: 12px;
  color: #8a7f74;
  margin-top: 2px;
`;

const BADGE_TONES = {
  green: { bg: "#e6f2ea", fg: "#1f6f4a" },
  purple: { bg: "#efe7f6", fg: "#6b3d86" },
  amber: { bg: "#fbf0dc", fg: "#8a5a12" },
  red: { bg: "#f8e4df", fg: "#a3402a" },
  gray: { bg: "#f0ebe4", fg: "#6b6259" },
};

export type BadgeTone = keyof typeof BADGE_TONES;

export const Badge = styled.span<{ $tone: BadgeTone }>`
  ${font}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $tone }) => BADGE_TONES[$tone].bg};
  color: ${({ $tone }) => BADGE_TONES[$tone].fg};
  white-space: nowrap;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const SearchInput = styled.input`
  ${font}
  padding: 9px 14px;
  border: 1.5px solid #e0d8ce;
  border-radius: 10px;
  font-size: 13.5px;
  outline: none;
  width: 280px;
  max-width: 100%;
  background: #faf8f5;
  box-sizing: border-box;

  &:focus {
    border-color: #111111;
    background: #ffffff;
  }
  @media (max-width: 700px) {
    font-size: 16px;
  }
`;

export const Chips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const Chip = styled.button<{ $active: boolean }>`
  ${font}
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? "#111111" : "#e0d8ce")};
  background: ${({ $active }) => ($active ? "#111111" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#4a423b")};
  cursor: pointer;
`;

export const Button = styled.button<{ $variant?: "primary" | "secondary" | "danger" }>`
  ${font}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 14px;
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid
    ${({ $variant }) => ($variant === "primary" ? "#111111" : $variant === "danger" ? "#e8c4ba" : "#e0d8ce")};
  background: ${({ $variant }) => ($variant === "primary" ? "#111111" : "#ffffff")};
  color: ${({ $variant }) =>
    $variant === "primary" ? "#ffffff" : $variant === "danger" ? "#a3402a" : "#2a241f"};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Select = styled.select`
  ${font}
  font-size: 13px;
  padding: 8px 10px;
  border: 1.5px solid #e0d8ce;
  border-radius: 10px;
  background: #ffffff;
  color: #111111;
  outline: none;
  @media (max-width: 700px) {
    font-size: 16px;
  }
`;

export const Empty = styled.div`
  ${font}
  font-size: 13.5px;
  color: #9a8e84;
  padding: 28px 0;
  text-align: center;
`;

export const Note = styled.p`
  ${font}
  font-size: 12.5px;
  color: #8a7f74;
  margin: 12px 0 0;
  line-height: 1.5;
`;

export const ErrorText = styled.p`
  ${font}
  font-size: 13px;
  color: #a3402a;
  margin: 8px 0 0;
`;

export const PageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const LinkButton = styled.button`
  ${font}
  font-size: 12.5px;
  font-weight: 700;
  color: #6b6259;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #111111;
  }
`;

export const Callout = styled.div`
  ${font}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: #fbf6d9;
  border: 1px solid #efe4a3;
  border-radius: 14px;
  padding: 14px 18px;
  font-size: 14px;
  color: #4a423b;
`;
